/**
 * Inquiry Metrics Helper
 * 
 * Centralized helper for all inquiry-related business logic and calculations.
 * This module provides consistent inquiry metrics throughout the application.
 * 
 * Features:
 * - Inquiry statistics and counts
 * - Qualification status management
 * - Conversion tracking (inquiry to offer)
 * - Lead source analytics
 * - Performance metrics
 * 
 * Usage:
 * import inquiryMetrics from '@/helper/inquiryMetrics'
 * const { getInquiryCount, getConversionRate } = inquiryMetrics()
 */

import store from '@/store'
import moment from 'moment'

export default function inquiryMetrics() {
  // Constants
  const QUALIFICATION_OPTIONS = ['Akzeptiert', 'Abgelehnt', 'Sonstiges', 'Unbekannt']
  
  // Get all inquiries from store
  function getInquiries() {
    return store.getters.inquiriesAllEntries || []
  }

  // Get all offers from store (for conversion metrics)
  function getOffers() {
    return store.getters.offerEntries || []
  }

  // Get all locations from store
  function getLocations() {
    return store.getters.locationsEntries || []
  }

  // Basic Inquiry Counts
  function getInquiryCount() {
    return getInquiries().length
  }

  function getUnqualifiedCount() {
    return getInquiries().filter(inquiry => inquiry.qualification === null).length
  }

  function getAcceptedCount() {
    return getInquiries().filter(inquiry => inquiry.qualification === 'Akzeptiert').length
  }

  function getRejectedCount() {
    return getInquiries().filter(inquiry => inquiry.qualification === 'Abgelehnt').length
  }

  function getUnknownCount() {
    return getInquiries().filter(inquiry => inquiry.qualification === 'Unbekannt').length
  }

  function getOtherCount() {
    return getInquiries().filter(inquiry => inquiry.qualification === 'Sonstiges').length
  }

  // Status Management
  function getInquiryStatus(inquiry) {
    if (!inquiry) return 'Unbekannt'
    
    // Check if offer has been sent
    if (inquiry.offers && inquiry.offers.length > 0) {
      return 'Angebot versendet'
    }
    
    // Check if inquiry was rejected
    if (inquiry.status === 'rejected') {
      return 'Abgelehnt'
    }
    
    // Check qualification status
    if (inquiry.qualification === 'Akzeptiert') {
      return 'Akzeptiert'
    }
    
    if (inquiry.qualification === 'Abgelehnt') {
      return 'Abgelehnt'
    }
    
    return 'Offen'
  }

  function getStatusVariant(inquiry) {
    if (!inquiry) return 'secondary'
    
    if (inquiry.offers && inquiry.offers.length > 0) {
      return 'success'
    }
    
    if (inquiry.qualification === 'Akzeptiert') {
      return 'success'
    }
    
    if (inquiry.qualification === 'Abgelehnt') {
      return 'danger'
    }
    
    if (inquiry.qualification === 'Unbekannt' || inquiry.qualification === 'Sonstiges') {
      return 'warning'
    }
    
    return 'primary'
  }

  function getButtonVariant(inquiry) {
    if (!inquiry) return 'primary'
    
    if (inquiry.qualification === 'Akzeptiert') {
      return 'success'
    }
    
    if (inquiry.qualification === 'Abgelehnt') {
      return 'danger'
    }
    
    if (inquiry.qualification === 'Unbekannt' || inquiry.qualification === 'Sonstiges') {
      return 'warning'
    }
    
    return 'primary'
  }

  // Inquiry Discovery
  function getNewestInquiry() {
    const inquiries = getInquiries()
    if (inquiries.length === 0) return null

    return [...inquiries].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]
  }

  function getInquiriesWithOffers() {
    return getInquiries().filter(inquiry => inquiry.offers && inquiry.offers.length > 0)
  }

  function isOfferable(inquiry) {
    return inquiry && inquiry.qualification === 'Akzeptiert' && (!inquiry.offers || inquiry.offers.length === 0)
  }

  // Conversion Metrics
  function getInquiryToOfferConversionRate() {
    const inquiries = getInquiries()
    if (inquiries.length === 0) return 0
    
    const inquiriesWithOffers = getInquiriesWithOffers()
    return Math.round((inquiriesWithOffers.length / inquiries.length) * 100)
  }

  function getQualificationRate() {
    const inquiries = getInquiries()
    if (inquiries.length === 0) return 0
    
    const qualifiedInquiries = inquiries.filter(i => i.qualification !== null)
    return Math.round((qualifiedInquiries.length / inquiries.length) * 100)
  }

  function getAcceptanceRate() {
    const inquiries = getInquiries()
    if (inquiries.length === 0) return 0
    
    const acceptedInquiries = inquiries.filter(i => i.qualification === 'Akzeptiert')
    return Math.round((acceptedInquiries.length / inquiries.length) * 100)
  }

  // Time-based Analytics
  function getInquiriesByPeriod(period = 'month') {
    const inquiries = getInquiries()
    const now = moment()
    let startDate

    switch (period) {
      case 'week':
        startDate = moment().subtract(1, 'week')
        break
      case 'month':
        startDate = moment().subtract(1, 'month')
        break
      case 'quarter':
        startDate = moment().subtract(3, 'months')
        break
      case 'year':
        startDate = moment().subtract(1, 'year')
        break
      default:
        startDate = moment().subtract(1, 'month')
    }

    return inquiries.filter(inquiry => {
      const inquiryDate = moment(inquiry.created_at)
      return inquiryDate.isBetween(startDate, now, null, '[]')
    })
  }

  function getInquiryTrend(months = 12) {
    const inquiries = getInquiries()
    const trendData = {}

    for (let i = months - 1; i >= 0; i -= 1) {
      const date = moment().subtract(i, 'months')
      const monthKey = date.format('YYYY-MM')
      const monthLabel = date.format('MMM', 'de')

      const inquiriesInMonth = inquiries.filter(inquiry => {
        const inquiryDate = moment(inquiry.created_at)
        return inquiryDate.format('YYYY-MM') === monthKey
      })

      trendData[monthLabel] = {
        count: inquiriesInMonth.length,
        accepted: inquiriesInMonth.filter(i => i.qualification === 'Akzeptiert').length,
        rejected: inquiriesInMonth.filter(i => i.qualification === 'Abgelehnt').length,
        withOffers: inquiriesInMonth.filter(i => i.offers && i.offers.length > 0).length,
        month: monthKey,
        label: monthLabel
      }
    }

    return trendData
  }

  // Lead Source Analytics
  function getInquiriesByLeadSource() {
    const inquiries = getInquiries()
    const leadSourceStats = {}

    inquiries.forEach(inquiry => {
      const source = inquiry.leadSource || 'Unbekannt'
      if (!leadSourceStats[source]) {
        leadSourceStats[source] = {
          count: 0,
          accepted: 0,
          rejected: 0,
          withOffers: 0,
          inquiries: []
        }
      }
      
      leadSourceStats[source].count += 1
      leadSourceStats[source].inquiries.push(inquiry)
      
      if (inquiry.qualification === 'Akzeptiert') {
        leadSourceStats[source].accepted += 1
      }
      if (inquiry.qualification === 'Abgelehnt') {
        leadSourceStats[source].rejected += 1
      }
      if (inquiry.offers && inquiry.offers.length > 0) {
        leadSourceStats[source].withOffers += 1
      }
    })

    return Object.entries(leadSourceStats)
      .map(([source, data]) => ({
        source,
        ...data,
        conversionRate: data.count > 0 ? Math.round((data.withOffers / data.count) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
  }

  function getTopLeadSources(limit = 5) {
    return getInquiriesByLeadSource().slice(0, limit)
  }

  // Location Analytics
  function getInquiriesByLocation() {
    const inquiries = getInquiries()
    const locations = getLocations()
    const locationStats = {}

    inquiries.forEach(inquiry => {
      const locationId = inquiry.storageLocation
      const location = locations.find(l => l.id === Number(locationId))
      const locationName = location ? location.name : 'Unbekannt'
      
      if (!locationStats[locationName]) {
        locationStats[locationName] = {
          count: 0,
          accepted: 0,
          rejected: 0,
          withOffers: 0,
          inquiries: []
        }
      }
      
      locationStats[locationName].count += 1
      locationStats[locationName].inquiries.push(inquiry)
      
      if (inquiry.qualification === 'Akzeptiert') {
        locationStats[locationName].accepted += 1
      }
      if (inquiry.qualification === 'Abgelehnt') {
        locationStats[locationName].rejected += 1
      }
      if (inquiry.offers && inquiry.offers.length > 0) {
        locationStats[locationName].withOffers += 1
      }
    })

    return Object.entries(locationStats)
      .map(([location, data]) => ({
        location,
        ...data,
        conversionRate: data.count > 0 ? Math.round((data.withOffers / data.count) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
  }

  // Response Time Analytics
  function getAverageResponseTime() {
    const inquiriesWithOffers = getInquiriesWithOffers()
    if (inquiriesWithOffers.length === 0) return 'N/A'
    
    let totalHours = 0
    let validCount = 0
    
    inquiriesWithOffers.forEach(inquiry => {
      if (inquiry.created_at && inquiry.offers[0] && inquiry.offers[0].created_at) {
        const inquiryDate = moment(inquiry.created_at)
        const offerDate = moment(inquiry.offers[0].created_at)
        const hours = offerDate.diff(inquiryDate, 'hours')
        
        if (hours >= 0) {
          totalHours += hours
          validCount += 1
        }
      }
    })
    
    if (validCount === 0) return 'N/A'
    
    const avgHours = Math.round(totalHours / validCount)
    if (avgHours < 24) {
      return `${avgHours} Stunden`
    }
    return `${Math.round(avgHours / 24)} Tage`
  }

  // Search and Filtering
  function searchInquiries(query) {
    if (!query || query.trim() === '') return getInquiries()

    const searchTerm = query.toLowerCase().trim()
    return getInquiries().filter(inquiry => {
      const firstName = (inquiry.firstName || '').toLowerCase()
      const lastName = (inquiry.lastName || '').toLowerCase()
      const email = (inquiry.email || '').toLowerCase()
      const message = (inquiry.message || '').toLowerCase()
      const organization = (inquiry.organizationName || '').toLowerCase()
      const id = String(inquiry.id || '')

      return firstName.includes(searchTerm)
             || lastName.includes(searchTerm)
             || email.includes(searchTerm)
             || message.includes(searchTerm)
             || organization.includes(searchTerm)
             || id.includes(searchTerm)
             || `${firstName} ${lastName}`.includes(searchTerm)
    })
  }

  function filterInquiriesByQualification(qualification) {
    if (!qualification) return getInquiries()
    
    if (qualification === 'unqualified') {
      return getInquiries().filter(i => i.qualification === null)
    }
    
    return getInquiries().filter(i => i.qualification === qualification)
  }

  function filterInquiriesByStatus(status) {
    const inquiries = getInquiries()
    
    switch (status) {
      case 'open':
        return inquiries.filter(i => !i.qualification && (!i.offers || i.offers.length === 0))
      case 'qualified':
        return inquiries.filter(i => i.qualification !== null)
      case 'with-offers':
        return inquiries.filter(i => i.offers && i.offers.length > 0)
      case 'accepted':
        return inquiries.filter(i => i.qualification === 'Akzeptiert')
      case 'rejected':
        return inquiries.filter(i => i.qualification === 'Abgelehnt')
      default:
        return inquiries
    }
  }

  // Organization vs Private Analysis
  function getOrganizationVsPrivateRatio() {
    const inquiries = getInquiries()
    const total = inquiries.length
    const organizations = inquiries.filter(i => i.isOrganization === true).length
    const privateInquiries = total - organizations

    return {
      total,
      organizations: {
        count: organizations,
        percentage: total > 0 ? Math.round((organizations / total) * 100) : 0
      },
      private: {
        count: privateInquiries,
        percentage: total > 0 ? Math.round((privateInquiries / total) * 100) : 0
      }
    }
  }

  // Summary Statistics
  function getInquirySummaryStats() {
    const inquiries = getInquiries()
    const withOffers = getInquiriesWithOffers()
    
    return {
      total: inquiries.length,
      unqualified: getUnqualifiedCount(),
      accepted: getAcceptedCount(),
      rejected: getRejectedCount(),
      unknown: getUnknownCount(),
      other: getOtherCount(),
      withOffers: withOffers.length,
      conversionRate: getInquiryToOfferConversionRate(),
      qualificationRate: getQualificationRate(),
      acceptanceRate: getAcceptanceRate(),
      averageResponseTime: getAverageResponseTime()
    }
  }

  // Test Data Generation Helper
  function generateTestInquiry() {
    const salutations = {
      male: ['Herr'],
      female: ['Frau']
    }
    const firstNames = {
      male: ['Benjamin', 'David', 'Fabian', 'Jan', 'Lukas', 'Max', 'Oliver', 'Paul'],
      female: ['Anna', 'Caroline', 'Emilia', 'Hannah', 'Lea', 'Sophie', 'Marie', 'Lisa']
    }
    const lastNames = ['Müller', 'Schmidt', 'Schneider', 'Fischer', 'Weber', 'Mayer', 'Wagner', 'Becker']
    const messages = [
      'Ich interessiere mich für einen Lagerraum.',
      'Benötige dringend Lagerfläche für Möbel.',
      'Suche Lagerplatz für Geschäftsunterlagen.',
      'Brauche kurzfristig einen Container.',
      'Anfrage für langfristige Lagerung.'
    ]
    const streets = ['Hauptstraße', 'Bahnhofstraße', 'Kirchstraße', 'Mühlenweg', 'Gartenweg']
    const cities = ['Münster', 'Berlin', 'Hamburg', 'München', 'Köln']
    const leadSources = ['Website', 'Empfehlung', 'Werbung', 'Messe', 'Google', 'Social Media']
    const organizationNames = ['Tech GmbH', 'Handel AG', 'Service KG', 'Logistik GmbH', '']

    const gender = Math.random() < 0.5 ? 'male' : 'female'
    const salutation = salutations[gender][Math.floor(Math.random() * salutations[gender].length)]
    const firstName = firstNames[gender][Math.floor(Math.random() * firstNames[gender].length)]
    const lastName = lastNames[Math.floor(Math.random() * lastNames.length)]
    const message = messages[Math.floor(Math.random() * messages.length)]
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.com`
    const streetNumber = Math.floor(Math.random() * 100) + 1
    const streetName = streets[Math.floor(Math.random() * streets.length)]
    const city = cities[Math.floor(Math.random() * cities.length)]
    const zipCode = String(48000 + Math.floor(Math.random() * 1000))
    const phoneNumber = `01${Math.floor(Math.random() * 90) + 10}-${Math.floor(Math.random() * 90000000) + 10000000}`
    const leadSource = leadSources[Math.floor(Math.random() * leadSources.length)]
    const storageLocation = String(Math.floor(Math.random() * 3) + 1)
    const estimatedStartRent = moment().add(Math.floor(Math.random() * 30), 'days').format('YYYY-MM-DD')
    const estimatedEndRent = moment().add(Math.floor(Math.random() * 180) + 30, 'days').format('YYYY-MM-DD')
    const organizationName = organizationNames[Math.floor(Math.random() * organizationNames.length)]
    const isOrganization = organizationName !== ''

    const storageJSON = {
      storage: [
        {
          id: 1,
          type: Math.random() > 0.5 ? 'Classic Lagercontainer' : 'Deluxe Lagercontainer',
          priceStorage: String(Math.floor(Math.random() * 200) + 50),
          numberStorage: Math.floor(Math.random() * 3) + 1,
          estimatedEndRent: `${Math.floor(Math.random() * 12) + 1} Monate`,
          estimatedStartRent: moment(estimatedStartRent).format('DD.MM.YYYY')
        }
      ]
    }

    return {
      firstName,
      lastName,
      message,
      email,
      salutation,
      street: `${streetName} ${streetNumber}`,
      city,
      zipCode,
      phoneNumber,
      leadSource,
      storageLocation,
      estimatedStartRent,
      estimatedEndRent,
      organizationName,
      storageJSON,
      isOrganization,
      country: 'Deutschland',
      acceptInquiry: false,
      qualification: null,
      status: 'open',
      offers: [],
      created_by: 'Test Generator',
      updated_by: 'Test Generator'
    }
  }

  // Export all functions
  return {
    // Constants
    QUALIFICATION_OPTIONS,
    
    // Basic counts
    getInquiryCount,
    getUnqualifiedCount,
    getAcceptedCount,
    getRejectedCount,
    getUnknownCount,
    getOtherCount,
    
    // Status management
    getInquiryStatus,
    getStatusVariant,
    getButtonVariant,
    isOfferable,
    
    // Discovery
    getNewestInquiry,
    getInquiriesWithOffers,
    
    // Conversion metrics
    getInquiryToOfferConversionRate,
    getQualificationRate,
    getAcceptanceRate,
    
    // Time-based analytics
    getInquiriesByPeriod,
    getInquiryTrend,
    
    // Lead source analytics
    getInquiriesByLeadSource,
    getTopLeadSources,
    
    // Location analytics
    getInquiriesByLocation,
    
    // Response time
    getAverageResponseTime,
    
    // Search and filtering
    searchInquiries,
    filterInquiriesByQualification,
    filterInquiriesByStatus,
    
    // Organization analysis
    getOrganizationVsPrivateRatio,
    
    // Summary
    getInquirySummaryStats,
    
    // Test data
    generateTestInquiry,
    
    // Raw data access
    getInquiries,
    getOffers,
    getLocations
  }
}
